import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"

const SSH_KEY = "ssh_settings"

export const DEFAULT_SSH = {
  host: "host.docker.internal",
  port: 22,
  username: "",
  password: "",
  projectDir: "/srv/projects/ai-Assistant",
  projectDirs: [],
  agent: "claude",
  mode: "floating",
  secondaries: [],
  reminderKey: "",
  reminderAgentKey: "",
}

export function loadSshSettings() {
  try {
    return { ...DEFAULT_SSH, ...JSON.parse(localStorage.getItem(SSH_KEY) ?? "{}") }
  } catch {
    return { ...DEFAULT_SSH }
  }
}

export function saveSshSettings(s) {
  localStorage.setItem(SSH_KEY, JSON.stringify(s))
}

export function getSecondaries() {
  const cfg = loadSshSettings()
  if (Array.isArray(cfg.secondaries) && cfg.secondaries.length > 0) return cfg.secondaries
  // Legacy: a single boolean flag implied `{username}_secondary`.
  if (cfg.has_secondary === true && cfg.username) return [`${cfg.username}_secondary`]
  return []
}

export function hasSshSecondary() {
  return getSecondaries().length > 0
}

// One-shot check: is the tmux session behind a disconnected ("idle") tab still
// alive on the host? Opens its own short-lived WebSocket (independent of the
// tab's own connection) and resolves `true`/`false`, or `null` if the check
// itself couldn't complete (network/auth error, timeout) — callers should treat
// `null` as "unknown, don't assume it's dead" and fall back to a normal reconnect.
export function checkTmuxSessionAlive({ host, port, username, password, sessionId }, timeoutMs = 8000) {
  return new Promise((resolve) => {
    let settled = false
    let timer = null
    const finish = (v) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      try { ws.close() } catch {}
      resolve(v)
    }
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/terminal/`)
    timer = setTimeout(() => finish(null), timeoutMs)
    ws.onopen = () => {
      ws.send(JSON.stringify({ t: "check_session", host, port, username, password, session_id: sessionId }))
    }
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        if (msg.t === "session_check") finish(!!msg.alive)
        else if (msg.t === "error") finish(null)
      } catch { finish(null) }
    }
    ws.onerror = () => finish(null)
    ws.onclose = () => finish(null)
  })
}

export function getSecondaryUsername() {
  return getSecondaries()[0] ?? ""
}

// `verzim_tertiary` → "Tertiary" (the ordinal suffix, capitalised).
export function tierLabel(username) {
  const suffix = username.slice(username.lastIndexOf("_") + 1)
  return suffix ? suffix.charAt(0).toUpperCase() + suffix.slice(1) : "Secondary"
}

export function getProjectDirs() {
  const cfg = loadSshSettings()
  if (cfg.projectDirs && cfg.projectDirs.length > 0) return cfg.projectDirs
  return cfg.projectDir ? [cfg.projectDir] : ["/srv/projects/ai-Assistant"]
}

export function getDefaultProjectDir() {
  return getProjectDirs()[0]
}

// Sibling projects the IDE can browse/edit (file tree + DESIGN tab). Kept apart
// from getProjectDirs() so the terminal's session semantics stay unchanged.
export const KNOWN_PROJECT_DIRS = [
  "/srv/projects/ai-Assistant",
  "/srv/projects/huts-heroes-dev",
  "/srv/projects/huts-heroes",
]

// Configured dirs first (default stays getProjectDirs()[0]), then the known
// siblings, de-duplicated — used only to populate the IDE directory datalist.
export function getKnownProjectDirs() {
  const seen = new Set()
  return [...getProjectDirs(), ...KNOWN_PROJECT_DIRS].filter((d) => {
    if (!d || seen.has(d)) return false
    seen.add(d)
    return true
  })
}

// ── Shared session list (single source of truth) ───────────────────────────────
// The SSH tab list lives here — not inside the floating window or the IDE side
// panel — so the *same* tabs render in whichever host is visible and survive
// navigating into/out of the IDE. The live xterm/WebSocket is owned by whichever
// host is mounted; on a host switch each tab reattaches to its existing tmux
// session via its persisted `sessionId`, so nothing is lost.

const SESSIONS_STORE_KEY = "terminal_sessions"

function loadStoredSessions() {
  try { return JSON.parse(localStorage.getItem(SESSIONS_STORE_KEY) ?? "[]") } catch { return [] }
}

function persistSessions(sessions) {
  if (sessions.length === 0) return
  const cfg = loadSshSettings()
  const toSave = sessions.map((s) => ({
    sessionId: s.sessionId ?? null,
    label: s.label,
    owner: s.profileOverride?.username ?? cfg.username,
    // Bind each tab to the directory + CLI workflow it was created with so a
    // reopen after a dropped connection or killed session recreates the SAME
    // tmux session (-c <dir>, <cmd>) instead of falling back to the default dir.
    projectDir: s.projectDirOverride ?? cfg.projectDir,
    startupCommand: s.startupCommand ?? "",
  }))
  localStorage.setItem(SESSIONS_STORE_KEY, JSON.stringify(toSave))
}

function clearStoredSessions() {
  localStorage.removeItem(SESSIONS_STORE_KEY)
}

// Remembered expanded/collapsed state of the IDE side-panel terminal — restored
// on every IDE entry so the user's last choice sticks (default: shown).
const IDE_TERM_KEY = "ide_terminal_open"

function loadIdeTermOpen() {
  try {
    const v = localStorage.getItem(IDE_TERM_KEY)
    return v === null ? true : JSON.parse(v) === true
  } catch {
    return true
  }
}

const Ctx = createContext(null)

export function TerminalProvider({ children }) {
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [mode, setModeState] = useState(() => loadSshSettings().mode ?? "floating")

  // When prefs are synced from server after login, reload the terminal mode
  useEffect(() => {
    const handler = () => {
      setModeState(loadSshSettings().mode ?? "floating")
    }
    window.addEventListener("ssh-settings-changed", handler)
    return () => window.removeEventListener("ssh-settings-changed", handler)
  }, [])

  // ── Session state — shared by the floating window and the IDE side panel ──────
  const [sessions, setSessions] = useState([])
  const [activeId, setActiveId] = useState(null)

  const openTerminal   = useCallback(() => { setOpen(true); setMinimized(false) }, [])
  const closeTerminal  = useCallback(() => setOpen(false), [])
  const toggleMinimize = useCallback(() => setMinimized((m) => !m), [])
  const setMode        = useCallback((m) => {
    setModeState(m)
    saveSshSettings({ ...loadSshSettings(), mode: m })
  }, [])

  // When the IDE hosts the terminal in its own side panel, the floating window is
  // hidden (`pinned`) and its prior open/minimized state is restored on release.
  // The IDE panel's own expanded/collapsed state is `ideTermOpen` (drives the
  // NavRail terminal icon colour while on the IDE page).
  const [pinned, setPinned] = useState(false)
  const [ideTermOpen, setIdeTermOpen] = useState(false)
  const priorRef = useRef(null)
  // Read open/minimized through refs so pin/unpin keep a stable identity — the IDE
  // mount effect depends on them, and a changing identity would re-fire it (losing
  // the captured prior state) the instant pinTerminal flips `open`.
  const openRef = useRef(open)
  const minimizedRef = useRef(minimized)
  useEffect(() => { openRef.current = open }, [open])
  useEffect(() => { minimizedRef.current = minimized }, [minimized])
  const pinTerminal = useCallback(() => {
    setPinned((already) => {
      if (!already) priorRef.current = { open: openRef.current, minimized: minimizedRef.current }
      return true
    })
    setOpen(false)
    setIdeTermOpen(loadIdeTermOpen())   // restore the remembered sidebar state
  }, [])
  const unpinTerminal = useCallback(() => {
    setPinned(false)
    setIdeTermOpen(false)   // leaving the IDE collapses the flag (not a user choice → not persisted)
    const prior = priorRef.current
    priorRef.current = null
    if (prior?.open) { setOpen(true); setMinimized(prior.minimized) }
  }, [])
  // User toggles persist the preference so it sticks across IDE visits + reloads.
  const openIdeTerm  = useCallback(() => {
    setIdeTermOpen(true)
    try { localStorage.setItem(IDE_TERM_KEY, "true") } catch {}
  }, [])
  const closeIdeTerm = useCallback(() => {
    setIdeTermOpen(false)
    try { localStorage.setItem(IDE_TERM_KEY, "false") } catch {}
  }, [])

  // Persist the tab list (sessionId/label/owner/dir/cmd) whenever it changes, plus
  // on teardown so the latest tmux session_ids survive an abrupt reload.
  const sessionsRef = useRef(sessions)
  useEffect(() => {
    sessionsRef.current = sessions
    if (sessions.length > 0) persistSessions(sessions)
  }, [sessions])
  useEffect(() => {
    const save = () => persistSessions(sessionsRef.current)
    const onVis = () => { if (document.visibilityState === "hidden") save() }
    window.addEventListener("pagehide", save)
    document.addEventListener("visibilitychange", onVis)
    return () => {
      window.removeEventListener("pagehide", save)
      document.removeEventListener("visibilitychange", onVis)
    }
  }, [])

  // When the network comes back, reattach idle/errored sessions to their existing
  // tmux session (the session_id is preserved) instead of leaving them stale.
  useEffect(() => {
    const onOnline = () => {
      setSessions((prev) => prev.map((s) =>
        (s.status === "idle" || s.status === "error")
          ? { ...s, reconnectToggle: s.reconnectToggle + 1 }
          : s))
    }
    window.addEventListener("online", onOnline)
    return () => window.removeEventListener("online", onOnline)
  }, [])

  // Restore sessions. The live registry (/api/ssh/sessions/active/) is the source
  // of truth for what is still running on the host, so we reattach to those rather
  // than spawning new tmux sessions; localStorage is the fallback (labels + ids).
  const restoreSessions = useCallback(async () => {
    const cfg = loadSshSettings()
    const stored = loadStoredSessions()
    const isMine = (owner) =>
      !!owner && !!cfg.username &&
      (owner === cfg.username || owner.startsWith(`${cfg.username}_`))

    let live = []
    try {
      const res = await fetch("/api/ssh/sessions/active/")
      if (res.ok) {
        const data = await res.json()
        live = (data.sessions ?? []).filter((s) => isMine(s.owner))
      }
    } catch { /* registry unreachable — fall back to localStorage */ }

    let descriptors
    if (live.length > 0) {
      const byId = new Map(stored.map((s) => [s.sessionId, s]))
      descriptors = live.map((s) => {
        const prev = byId.get(s.session_id)
        return {
          sessionId: s.session_id,
          label: prev?.label,
          owner: s.owner,
          projectDir: prev?.projectDir,
          startupCommand: prev?.startupCommand,
        }
      })
    } else {
      descriptors = stored
    }

    const mk = (s, i) => {
      const owner = s.owner
      const isSecondary = owner && owner !== cfg.username
      const startupCommand = s.startupCommand ?? (i === 0 ? cfg.agent || "" : "")
      return {
        id: Date.now() + i, status: "idle", errMsg: "", reconnectToggle: 0, attention: false,
        label: s.label || (i === 0 ? cfg.agent || "agent" : `shell ${i}`),
        sessionId: s.sessionId || null,
        startupCommand,
        profileOverride: isSecondary ? { username: owner, password: cfg.password } : undefined,
        projectDirOverride: s.projectDir ?? cfg.projectDir,
      }
    }

    if (descriptors.length > 0) {
      const restored = descriptors.map(mk)
      setSessions(restored)
      setActiveId(restored[0].id)
    } else {
      const id = Date.now()
      setSessions([{
        id, status: "idle", errMsg: "", reconnectToggle: 0, attention: false,
        label: cfg.agent || "agent", startupCommand: cfg.agent || "", sessionId: null,
      }])
      setActiveId(id)
    }
  }, [])

  // Lazily restore the tab list the first time a host (floating window or IDE
  // panel) needs to show it. Sessions then persist in memory across open/close
  // and across IDE entry/exit — only an explicit close-all clears them.
  const restoringRef = useRef(false)
  const ensureSessions = useCallback(() => {
    if (sessionsRef.current.length > 0 || restoringRef.current) return
    restoringRef.current = true
    restoreSessions().finally(() => { restoringRef.current = false })
  }, [restoreSessions])

  const addShellSession = useCallback((profileOverride, projectDir) => {
    const id = Date.now()
    const cfg = loadSshSettings()
    setSessions((prev) => {
      const shellCount = prev.filter((s) => s.label?.startsWith("shell")).length + 1
      const label = profileOverride ? `[${profileOverride.label}] shell ${shellCount}` : `shell ${shellCount}`
      const dir = projectDir ?? cfg.projectDir
      return [...prev, {
        id, status: "idle", errMsg: "", reconnectToggle: 0, attention: false,
        label, sessionId: null, startupCommand: "",
        profileOverride: profileOverride ? { username: profileOverride.username, password: profileOverride.password } : undefined,
        projectDirOverride: dir !== cfg.projectDir ? dir : undefined,
      }]
    })
    setActiveId(id)
    return id
  }, [])

  const addAgentSession = useCallback((profileName, username, password, projectDir) => {
    const id = Date.now()
    const cfg = loadSshSettings()
    const agent = cfg.agent || "agent"
    const label = `[${profileName}] ${agent}`
    const dir = projectDir ?? cfg.projectDir
    setSessions((prev) => [...prev, {
      id, status: "idle", errMsg: "", reconnectToggle: 0, attention: false,
      label, sessionId: null, startupCommand: agent,
      profileOverride: { username, password },
      projectDirOverride: dir !== cfg.projectDir ? dir : undefined,
    }])
    setActiveId(id)
    return id
  }, [])

  // Remove a tab. When the last tab goes, the shared session list is cleared and
  // every host is dismissed (floating closed + IDE panel collapsed); otherwise the
  // active tab falls back to the last remaining one.
  const closeSession = useCallback((id) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id)
      if (next.length === 0) {
        clearStoredSessions()
        setOpen(false)
        setIdeTermOpen(false)
        setActiveId(null)
        return next
      }
      setActiveId((cur) => (cur === id ? next[next.length - 1].id : cur))
      return next
    })
  }, [])

  const reconnectSession = useCallback((id) => {
    setSessions((prev) => prev.map((s) =>
      s.id === id ? { ...s, reconnectToggle: s.reconnectToggle + 1 } : s))
  }, [])

  const updateStatus = useCallback((id, status, errMsg = "") => {
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, status, errMsg } : s))
  }, [])
  const updateAttention = useCallback((id, attention) => {
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, attention } : s))
  }, [])
  const updateSessionId = useCallback((id, sessionId) => {
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, sessionId } : s))
  }, [])

  return (
    <Ctx.Provider value={{
      open, minimized, openTerminal, closeTerminal, toggleMinimize, mode, setMode,
      pinned, pinTerminal, unpinTerminal,
      ideTermOpen, openIdeTerm, closeIdeTerm,
      sessions, activeId, setActiveId,
      ensureSessions, addShellSession, addAgentSession, closeSession, reconnectSession,
      updateStatus, updateAttention, updateSessionId,
    }}>
      {children}
    </Ctx.Provider>
  )
}

export function useTerminal() {
  return useContext(Ctx)
}
